/**
 * 조건 설정 허브 — 도메인별 정책 진입 카드
 *
 *  운영자가 어떤 도메인의 조건/정책을 바꾸고 싶은지 한 화면에서 고름.
 *  현재는 크레딧 정책 1개만 활성화. 향후 알림·예약·계약서 등 추가될 때
 *  카드 배열에 1줄만 더하면 됨.
 */
import { Link } from 'react-router-dom';
import { Coins, Bell, Calendar, FileText, Settings } from 'lucide-react';
import { ROUTES } from '@/constants/routes';

interface DomainCard {
  id: string;
  title: string;
  desc: string;
  icon: React.ComponentType<{ className?: string }>;
  to?: string;
  /** 활성 카드면 정책 개수 표시 */
  countLabel?: string;
  /** 비활성(준비중) 카드면 isDisabled = true */
  isDisabled?: boolean;
  accent: 'orange' | 'blue' | 'green' | 'purple' | 'gray';
}

const DOMAINS: DomainCard[] = [
  {
    id: 'credit',
    title: '크레딧 조건',
    desc: '환산 기준 단가·환불·취소·만료·휴면 등 크레딧 운영 정책 (29개 항목)',
    icon: Coins,
    to: ROUTES.CONDITIONS_CREDIT,
    countLabel: '29개 정책',
    accent: 'orange',
  },
  {
    id: 'notification',
    title: '알림 조건',
    desc: '푸시 발송 시간대·빈도 제한·재발송 룰 등 (준비 중)',
    icon: Bell,
    isDisabled: true,
    accent: 'blue',
  },
  {
    id: 'reservation',
    title: '예약 조건',
    desc: '최소/최대 예약 가능 시간·취소 가능 기간 등 (준비 중)',
    icon: Calendar,
    isDisabled: true,
    accent: 'green',
  },
  {
    id: 'contract',
    title: '계약서 조건',
    desc: '자동 발송 룰·만료 일수·재발송 정책 등 (준비 중)',
    icon: FileText,
    isDisabled: true,
    accent: 'purple',
  },
];

const ACCENT_CLASSES: Record<DomainCard['accent'], { border: string; bg: string; text: string; icon: string }> = {
  orange: {
    border: 'border-orange-200',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    icon: 'text-orange-500',
  },
  blue: {
    border: 'border-blue-200',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    icon: 'text-blue-500',
  },
  green: {
    border: 'border-green-200',
    bg: 'bg-green-50',
    text: 'text-green-700',
    icon: 'text-green-500',
  },
  purple: {
    border: 'border-purple-200',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    icon: 'text-purple-500',
  },
  gray: {
    border: 'border-gray-200',
    bg: 'bg-gray-50',
    text: 'text-gray-700',
    icon: 'text-gray-500',
  },
};

export function ConditionsHubPage() {
  return (
    <div className="p-6 space-y-6 max-w-[1200px]">
      <header>
        <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-2">
          <Settings className="w-6 h-6 text-gray-700" />
          조건 설정
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          운영 정책 수치를 도메인별로 관리합니다. 카드를 선택하면 해당 영역의 조건 페이지로 이동합니다.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {DOMAINS.map((d) => {
          const accent = ACCENT_CLASSES[d.accent];
          const Icon = d.icon;

          const cardInner = (
            <div
              className={`flex items-start gap-4 p-5 rounded-xl border ${accent.border} ${
                d.isDisabled
                  ? 'bg-gray-50 opacity-60 cursor-not-allowed'
                  : `${accent.bg} hover:shadow-md transition-shadow cursor-pointer`
              }`}
            >
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-lg bg-white flex items-center justify-center ${accent.icon}`}
              >
                <Icon className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className={`text-base font-bold ${d.isDisabled ? 'text-gray-500' : accent.text}`}>
                    {d.title}
                  </h2>
                  {d.countLabel && (
                    <span
                      className={`text-[11px] font-mono px-2 py-0.5 rounded-full bg-white ${accent.text} border ${accent.border}`}
                    >
                      {d.countLabel}
                    </span>
                  )}
                  {d.isDisabled && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                      준비 중
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-600 leading-relaxed">{d.desc}</p>
              </div>
            </div>
          );

          if (d.isDisabled || !d.to) {
            return <div key={d.id}>{cardInner}</div>;
          }
          return (
            <Link key={d.id} to={d.to}>
              {cardInner}
            </Link>
          );
        })}
      </div>

      <div className="text-xs text-gray-500 border-t border-gray-100 pt-4">
        💡 새 도메인 조건이 필요하면 운영팀에 요청해 주세요. 정책 수치 추가는 BE 시드 SQL 1행이면 가능합니다.
      </div>
    </div>
  );
}
